import { localeDomainMap } from "./domains";
import { brandTranslations } from "./routeTranslations";

/**
 * Simple function to get the base URL for a given locale
 * Uses existing getDomainWithoutLocale logic and localeDomainMap
 *
 * @param locale - The locale (e.g., 'nl', 'de', 'en-gb')
 * @returns The complete base URL (e.g., 'https://www.otrium.nl')
 */
export const getBaseUrl = (locale = "nl"): string => {
  const isProd = process.env.RELEASE_STAGE === "production";
  const domainWithoutLocale = isProd
    ? "https://www.otrium."
    : "https://staging.otrium.";

  const tld = localeDomainMap[locale] || "com";

  return `${domainWithoutLocale}${tld}`;
};

/**
 * Get the current shop's base URL based on environment or current locale
 * @returns The base URL for the current shop
 */
export const getCurrentShopBaseUrl = (): string => {
  // Try to detect locale from current URL if on client side
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    const tld = hostname.split(".").pop();

    // Find locale by TLD
    const foundLocale = Object.entries(localeDomainMap).find(
      ([, domain]) => domain === tld
    )?.[0];

    if (foundLocale) {
      return getBaseUrl(foundLocale);
    }
  }

  // Fallback to environment locale or default
  const locale = process.env.LOCALE || "nl";
  return getBaseUrl(locale);
};

/**
 * Convert a relative URL to absolute URL for current shop
 * @param relativeUrl - The relative URL (e.g., '/product/some-product')
 * @returns The absolute URL
 */
export const toAbsoluteUrl = (
  relativeUrl: string | undefined
): string | undefined => {
  if (!relativeUrl) {
    return undefined;
  }

  const baseUrl = getCurrentShopBaseUrl();
  const cleanUrl = relativeUrl.startsWith("/")
    ? relativeUrl
    : `/${relativeUrl}`;
  return `${baseUrl}${cleanUrl}`;
};

/**
 * Get the path by type and gender
 * @param type - The type of the path (e.g., 'brand', 'category')
 * @param gender - The gender of the path (e.g., 'men', 'women')
 * @param slug - The slug of the path (e.g., 'some-brand')
 * @param locale - The locale of the path (e.g., 'en', 'fr')
 * @returns The path by type and gender
 */
export const getPathByTypeAndGender = ({
  type,
  gender,
  slug,
  locale,
}: {
  type: string;
  gender: string;
  slug: string;
  locale: string;
}) => {
  if (type === "brand") {
    return `/${
      brandTranslations[locale as keyof typeof brandTranslations]
    }/${slug}/${gender}`;
  } else if (type === "category") {
    return `/${gender}/${slug}`;
  }
  return undefined;
};

/**
 * Get all brand paths
 * @param locale - The locale of the path (e.g., 'en', 'fr')
 * @returns The all brand paths
 */
export const getAllBrandPath = (locale: string) => {
  return brandTranslations[locale as keyof typeof brandTranslations];
};
